import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/components/PageHeader";
import { Calendar } from "@/components/ui/calendar";
import MedColorDot from "@/components/MedColorDot";
import EmptyState from "@/components/EmptyState";
import { getAnalytics, getToday } from "@/lib/api";
import { fmtDate, fmtTime12, doseLabel } from "@/lib/format";
import { localDateStr } from "@/lib/dates";
import { CalendarDays, Check, X } from "lucide-react";
import { parseISO, format } from "date-fns";

export default function CalendarPage() {
  const [selected, setSelected] = useState(new Date());
  const { data: analytics } = useQuery({ queryKey: ["analytics", 90], queryFn: () => getAnalytics(90) });
  const dateStr = localDateStr(selected);
  const { data: day } = useQuery({ queryKey: ["today", dateStr], queryFn: () => getToday(dateStr) });

  const { perfect, partial, missed, logged } = useMemo(() => {
    const p = [], pa = [], mi = [], lg = [];
    (analytics?.trend || []).forEach((t) => {
      const d = parseISO(t.date);
      if (t.expected === 0) {
        // No scheduled meds due this day, so "adherence" doesn't apply --
        // but as-needed activity is still real activity, and a purely
        // as-needed medication list would otherwise never show a single
        // dot anywhere on the calendar, ever.
        if (t.prn_taken > 0) lg.push(d);
        return;
      }
      if (t.adherence === 100) p.push(d);
      else if (t.taken > 0) pa.push(d);
      else mi.push(d);
    });
    return { perfect: p, partial: pa, missed: mi, logged: lg };
  }, [analytics]);

  return (
    <div>
      <PageHeader title="Calendar" subtitle="Your adherence history" />
      <div className="px-4 space-y-4">
        <div className="card-soft p-2 flex justify-center" data-testid="adherence-calendar">
          <Calendar
            mode="single" selected={selected} onSelect={(d) => d && setSelected(d)}
            modifiers={{ perfect, partial, missed, logged }}
            modifiersClassNames={{
              perfect: "relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1.5 after:w-1.5 after:rounded-full after:bg-[hsl(var(--success))]",
              partial: "relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1.5 after:w-1.5 after:rounded-full after:bg-[hsl(var(--warning))]",
              missed: "relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1.5 after:w-1.5 after:rounded-full after:bg-destructive",
              logged: "relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1.5 after:w-1.5 after:rounded-full after:bg-[hsl(var(--info))]",
            }}
          />
        </div>

        <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground flex-wrap">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[hsl(var(--success))]" />Perfect</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[hsl(var(--warning))]" />Partial</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-destructive" />Missed</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[hsl(var(--info))]" />As needed</span>
        </div>

        <div>
          <p className="px-1 font-display text-lg font-semibold mb-2">{fmtDate(selected, "EEEE, MMMM d")}</p>
          {(day?.doses || []).length === 0 && (day?.prn_logs || []).length === 0 ? (
            <EmptyState icon={CalendarDays} title="No doses" description="Nothing was scheduled or logged on this day." />
          ) : (
            <div className="space-y-2.5">
              {(day?.doses || []).map((d) => (
                <div key={d.id} className="card-soft p-3 flex items-center gap-3">
                  <MedColorDot color={d.color} size={40} />
                  <div className="flex-1 min-w-0"><p className="font-semibold truncate">{d.name}</p><p className="text-xs text-muted-foreground">{fmtTime12(d.time)} · {doseLabel(d.strength, d.unit)}</p></div>
                  {(d.status === "taken" || d.status === "partial") ? <span className="text-[hsl(var(--success))]"><Check className="h-5 w-5" /></span>
                    : d.status === "pending" ? <span className="text-xs text-muted-foreground">pending</span>
                    : <span className="text-destructive"><X className="h-5 w-5" /></span>}
                </div>
              ))}
              {/* As-needed doses aren't on a fixed schedule, so they never show up in
                  `doses` above -- this is the actual history of what was logged that
                  day, not just which PRN meds exist (that's a separate, undated list
                  used by the Today page's quick-log buttons). */}
              {(day?.prn_logs || []).map((l) => (
                <div key={l.id} className="card-soft p-3 flex items-center gap-3">
                  <MedColorDot color={l.color} size={40} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{l.name}</p>
                    <p className="text-xs text-muted-foreground">{format(parseISO(l.timestamp), "h:mm a")} · {doseLabel(l.dose_taken ?? l.strength, l.unit)} · as needed</p>
                  </div>
                  {(l.status === "taken" || l.status === "partial") ? <span className="text-[hsl(var(--success))]"><Check className="h-5 w-5" /></span>
                    : <span className="text-destructive"><X className="h-5 w-5" /></span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
